/**
 * Backend to Form Value Transformation
 *
 * This module handles the transformation of backend API responses into
 * form-friendly values. The main transformations are:
 *
 * 1. Converting object maps to arrays with 'key' property
 * 2. Adding _internalId to all editable items for stable React keys
 * 3. Converting backend status keys to frontend status keys
 * 4. Sorting actions and tasks by order
 */
import { objectToKeyedArray } from "src/cases/templates/formUtils/objectArrayConverters";
import {
  buildBackendStatusKey,
  createStatusKeyMappings,
} from "src/cases/templates/formUtils/statusKeyMapping";
import {
  CaseTemplateActionFormValue,
  CaseTemplateFormValues,
  CaseTemplateTaskFormValue,
  CaseTemplateWorkflowRuleFormValue,
  EnumPropertyValueForm,
  FileCategoryFormValue,
} from "src/cases/templates/formUtils/types";
import { generateInternalId } from "src/cases/templates/internalIdUtils";
import {
  CasePropertiesValue,
  CaseTemplateActionOutput,
  CaseTemplateTaskOutput,
  CaseTemplateVersionCopilotSettings,
  CaseTemplateVersionCopilotSettingsOperatingModeEnum,
  CaseTemplateWorkflowRuleActionAutomation,
  CaseTemplateWorkflowRuleOutput,
  JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel,
} from "src/clients/case-management";
import { logger } from "src/utils/logger";

// =============================================================================
// COPILOT SETTINGS DEFAULT
// =============================================================================

export const DEFAULT_COPILOT_SETTINGS: CaseTemplateVersionCopilotSettings = {
  copilot_enabled: false,
  copilot_prompt: "",
  websearch_tool_enabled: false,
  operating_mode: CaseTemplateVersionCopilotSettingsOperatingModeEnum.LAZY,
};

// =============================================================================
// INTERNAL ID ENRICHMENT
// These functions add _internalId to items for stable form tracking.
// =============================================================================

/**
 * Adds _internalId to status values and their sub_values.
 * These IDs are used for stable React keys and workflow rule mapping.
 *
 * @param status - The status enum property from case_properties
 * @returns Status with _internalId on each value and sub_value
 */
const enrichStatusesWithInternalIds = (
  status: Extract<CasePropertiesValue, { type: "enum" }> | undefined,
): EnumPropertyValueForm | undefined => {
  if (!status) {
    return undefined;
  }
  return {
    ...status,
    values: status.values.map((value) => ({
      _internalId: generateInternalId(),
      ...value,
      sub_values: value.sub_values?.map((subValue) => ({
        _internalId: generateInternalId(),
        ...subValue,
      })),
    })),
  };
};

/**
 * Transforms actions object to sorted array with internal IDs.
 *
 * @param actions - Actions object map from backend
 * @returns Array sorted by order field with _internalId added
 */
const enrichActionsWithInternalIds = (actions: {
  [key: string]: CaseTemplateActionOutput;
}): CaseTemplateActionFormValue[] => {
  return objectToKeyedArray(actions)
    .map((action) => ({
      ...action,
      _internalId: generateInternalId(),
    }))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

/**
 * Transforms tasks object to sorted array with internal IDs.
 *
 * @param tasks - Tasks object map from backend (may be undefined)
 * @returns Array sorted by order field with _internalId added
 */
const enrichTasksWithInternalIds = (
  tasks: { [key: string]: CaseTemplateTaskOutput } | undefined,
): CaseTemplateTaskFormValue[] => {
  return objectToKeyedArray(tasks || {})
    .map((task) => ({
      ...task,
      _internalId: generateInternalId(),
    }))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

// =============================================================================
// KEY MAPPING HELPERS
// These functions create lookup maps for linking workflow rules to items.
// =============================================================================

/**
 * Creates lookup maps from task/action keys to their internal IDs.
 * Used to link workflow rules to their actual tasks/actions.
 *
 * @param tasks - Array of tasks with _internalId
 * @param actions - Array of actions with _internalId
 * @returns Object with taskKeyToId and actionKeyToId Maps
 */
const createTaskActionIdLookups = (
  tasks: CaseTemplateTaskFormValue[],
  actions: CaseTemplateActionFormValue[],
): { taskKeyToId: Map<string, string>; actionKeyToId: Map<string, string> } => {
  const taskKeyToId = new Map<string, string>();
  const actionKeyToId = new Map<string, string>();

  tasks.forEach((task) => {
    if (task.key) {
      taskKeyToId.set(task.key, task._internalId);
    }
  });

  actions.forEach((action) => {
    if (action.key) {
      actionKeyToId.set(action.key, action._internalId);
    }
  });

  return { taskKeyToId, actionKeyToId };
};

// =============================================================================
// WORKFLOW RULE TRANSFORMATION
// Complex transformation that maps backend rules to form format.
// =============================================================================

/**
 * Finds the sub_status automation entry in an automation array.
 * Used to combine status and sub_status into a single frontend value.
 */
const findSubStatusAutomation = (
  automation: CaseTemplateWorkflowRuleActionAutomation[],
): CaseTemplateWorkflowRuleActionAutomation | undefined => {
  return automation.find((a) => a.field === "sub_status");
};

/**
 * Transforms workflow rules from backend format to form format.
 *
 * Key transformations:
 * 1. Maps rule keys from backend format ("open-new") to frontend format ("uuid#uuid")
 * 2. Adds _internalId to workflow tasks and actions referencing actual item IDs
 * 3. Converts status automation values from backend to frontend format
 * 4. Removes sub_status automation fields (frontend handles status+substatus as single value)
 *
 * @param workflowRules - Backend workflow rules object
 * @param backendToFrontendStatusKeys - Map from backend to frontend status keys
 * @param taskKeyToId - Map from task keys to internal IDs
 * @param actionKeyToId - Map from action keys to internal IDs
 * @returns Workflow rules in form format with frontend status keys
 */
const transformWorkflowRulesToFormFormat = (
  workflowRules: { [key: string]: CaseTemplateWorkflowRuleOutput } | undefined,
  backendToFrontendStatusKeys: Map<string, string>,
  taskKeyToId: Map<string, string>,
  actionKeyToId: Map<string, string>,
): CaseTemplateWorkflowRuleFormValue => {
  const result = Object.fromEntries(
    Object.entries(workflowRules || {}).map(([backendStatusKey, rule]) => {
      // Convert backend status key to frontend format
      const frontendStatusKey =
        backendToFrontendStatusKeys.get(backendStatusKey) || "";

      return [
        frontendStatusKey,
        {
          ...rule,
          // Add internal IDs to workflow tasks
          workflow_tasks_keys: rule.workflow_tasks_keys?.map((task) => ({
            ...task,
            _internalId: taskKeyToId.get(task.task_key) || "",
          })),
          // Transform actions with internal IDs and automation value mapping
          actions: rule.actions?.map((action) => ({
            ...action,
            _internalId: actionKeyToId.get(action.action_key) || "",
            // Add internal IDs to required tasks
            required_tasks_keys: action.required_tasks_keys?.map((reqTask) => ({
              ...reqTask,
              _internalId: taskKeyToId.get(reqTask.task_key) || "",
            })),
            // Transform automation values
            automation: action.automation
              ?.map((automation) => {
                if (automation.field === "status") {
                  // Convert backend status value to frontend format
                  const subStatusValue = findSubStatusAutomation(
                    action.automation,
                  );

                  return {
                    ...automation,
                    value:
                      backendToFrontendStatusKeys.get(
                        buildBackendStatusKey({
                          status: String(automation.value),
                          subStatus: subStatusValue?.value
                            ? String(subStatusValue?.value)
                            : undefined,
                        }),
                      ) || "",
                  };
                }

                if (automation.field === "sub_status") {
                  // Remove sub_status for frontend - we combine it with status
                  return undefined;
                }

                return automation;
              })
              .filter(
                (automation): automation is NonNullable<typeof automation> =>
                  automation !== undefined,
              ),
          })),
        },
      ];
    }),
  );

  return result;
};

// =============================================================================
// MAIN ENTRY POINT
// =============================================================================

/**
 * Transforms a backend template response to form values.
 *
 * This is the main entry point for Backend -> Form transformation.
 * Called when loading template data into the form.
 *
 * @param template - Backend API response for template version
 * @returns Form values ready for React Hook Form
 *
 * @example
 * const { data } = useCaseTemplateVersion({ templateId, versionId });
 * const formValues = transformTemplateToFormValues(data);
 */
export const transformTemplateToFormValues = (
  template: JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel,
): CaseTemplateFormValues => {
  // Transform file categories and mark the default one
  const fileCategories = objectToKeyedArray(
    template.attributes.file_categories,
  ) as FileCategoryFormValue[];

  fileCategories.forEach((category) => {
    if (category.key === "other_files") {
      (category as FileCategoryFormValue)._isDefault = true;
    }
  });

  // Transform actions and tasks with internal IDs
  const actionsWithIds = enrichActionsWithInternalIds(
    template.attributes.case_actions,
  );
  const tasksWithIds = enrichTasksWithInternalIds(
    template.attributes.case_tasks,
  );

  // Create key-to-ID mappings for workflow rules
  const { taskKeyToId, actionKeyToId } = createTaskActionIdLookups(
    tasksWithIds,
    actionsWithIds,
  );

  // Transform status property with internal IDs
  const statusProperty = template.attributes.case_properties?.status;
  const hasValidStatus = statusProperty?.type === "enum";

  if (!hasValidStatus) {
    // This should never happen, as the template validation should catch this.
    // But we still shot template editor, so user can add missing status property.
    logger.error(
      "Invalid template: `status` property must exist and be an enum type",
    );
  }

  const statusesWithIds = hasValidStatus
    ? enrichStatusesWithInternalIds(statusProperty)
    : undefined;

  // Build case properties array with enriched status
  const casePropertiesWithIds = objectToKeyedArray({
    ...template.attributes.case_properties,
    ...(statusesWithIds && { status: statusesWithIds }),
  });

  // Create status key mappings for workflow rule transformation
  const statusKeyMappings = statusesWithIds
    ? createStatusKeyMappings(statusesWithIds.values)
    : {
        backendToFrontend: new Map<string, string>(),
        frontendToBackend: new Map<string, string>(),
      };

  // Transform workflow rules with frontend status keys
  const workflowRulesWithIds = transformWorkflowRulesToFormFormat(
    template.attributes.workflow_rules,
    statusKeyMappings.backendToFrontend,
    taskKeyToId,
    actionKeyToId,
  );

  return {
    ...template,
    attributes: {
      ...template.attributes,
      case_properties: casePropertiesWithIds,
      file_categories: fileCategories,
      case_actions: actionsWithIds,
      workflow_rules: workflowRulesWithIds as CaseTemplateWorkflowRuleFormValue,
      case_tasks: tasksWithIds,
      case_blocks_configs: objectToKeyedArray(
        template.attributes.case_blocks_configs,
      ).map((block) => ({
        ...block,
        _internalId: generateInternalId(),
      })),
      copilot_settings:
        template.attributes.copilot_settings ?? DEFAULT_COPILOT_SETTINGS,
    },
  };
};
